"use client";

import React, { useState, useEffect, useRef } from "react";
import "./RangeSlider.css";

interface RangeSliderProps {
  defaultMin: number;
  defaultMax: number;
  maxLimit?: number;
  label?: string;
  prefix?: string;
  initialValue?: [number | null, number | null]; // [minValue, maxValue]
  onChange?: (values: { min: number | null; max: number | null }) => void;
  onApply?: (values: { min: number | null; max: number | null }) => void;
  showApplyButton?: boolean;
  inputClassName?: string; // For custom input styling
  reset?: () => void; // External reset callback
  type?: string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  defaultMin,
  defaultMax,
  maxLimit,
  label = "PRICE RANGE",
  prefix = "$",
  initialValue,
  onChange,
  onApply,
  showApplyButton = false,
  inputClassName = "",
  reset: externalReset,
  type = "Desk Price",
}) => {
  const propMin = defaultMin;
  const propMax = defaultMax;


  // Extract initial values from prop
  // If initialValue is an empty array or [0, 0], treat it as default state (no initial values)
  const hasInitialValues = initialValue && initialValue.length > 0 && !(initialValue[0] === 0 && initialValue[1] === 0);
  const initialMinValue = hasInitialValues ? initialValue[0] : undefined;
  const initialMaxValue = hasInitialValues ? initialValue[1] : undefined;

  // Determine initial range (extend if initial values exceed default range)
  const getInitialRangeMin = () => {
    if (initialMinValue !== null && initialMinValue !== undefined && initialMinValue < propMin) {
      return initialMinValue;
    }
    return propMin;
  };

  const getInitialRangeMax = () => {
    if (initialMaxValue !== null && initialMaxValue !== undefined && initialMaxValue > propMax) {
      return initialMaxValue;
    }
    return propMax;
  };

  // State for the actual range boundaries (can be extended)
  const [rangeMin, setRangeMin] = useState(getInitialRangeMin());
  const [rangeMax, setRangeMax] = useState(getInitialRangeMax());

  // State for current selected values
  const [minValue, setMinValue] = useState<number>(
    initialMinValue !== null && initialMinValue !== undefined ? initialMinValue : propMin
  );
  const [maxValue, setMaxValue] = useState<number>(
    initialMaxValue !== null && initialMaxValue !== undefined ? initialMaxValue : propMax
  );

  // Helper function to format number as currency with k/M/T for buttons
  const formatCurrency = (value: number): string => {
    if (value >= 1000000000000) { // Trillion
      return `${(value / 1000000000000).toFixed(value % 1000000000000 === 0 ? 0 : 1)}T`;
    }
    if (value >= 1000000) { // Million
      return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`;
    }
    if (value >= 1000) { // Thousand
      return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
    }
    return value.toLocaleString();
  };

  // Helper function to format number with comma separators for input fields
  const formatInputCurrency = (value: number): string => {
    return value.toLocaleString('en-US');
  };

  // State for input field values (strings to handle empty states)
  const [minInputValue, setMinInputValue] = useState<string>(
    initialMinValue !== null && initialMinValue !== undefined ? formatInputCurrency(initialMinValue) : ""
  );
  const [maxInputValue, setMaxInputValue] = useState<string>(
    initialMaxValue !== null && initialMaxValue !== undefined ? formatInputCurrency(initialMaxValue) : ""
  );

  // State for "No Min" / "No Max" display
  const [showNoMin, setShowNoMin] = useState(initialMinValue === null || initialMinValue === undefined);
  const [showNoMax, setShowNoMax] = useState(initialMaxValue === null || initialMaxValue === undefined);

  // State to track which thumb was last changed (for z-index layering)
  const [lastChangedThumb, setLastChangedThumb] = useState<'min' | 'max'>('max');

  // State to track which input is focused
  const [minInputFocused, setMinInputFocused] = useState(false);
  const [maxInputFocused, setMaxInputFocused] = useState(false);

  // Refs for slider inputs
  const minSliderRef = useRef<HTMLInputElement>(null);
  const maxSliderRef = useRef<HTMLInputElement>(null);
  const isInitialMount = useRef(true);
  const shouldResetRangeOnDrag = useRef(true); // Track if we should reset range on next drag

  // Threshold percentage for showing "No Min" / "No Max"
  const THRESHOLD_PERCENT = 2; // 2% from the edge

  // Minimum gap between min and max values to prevent thumb overlap
  const MIN_GAP = Math.max(1, Math.ceil((propMax - propMin) * 0.005)); // 0.5% of range or minimum 1

  // Helper function to parse currency string back to number
  const parseCurrency = (value: string): number => {
    // Remove commas and other formatting
    const cleaned = value.replace(/,/g, '');

    // Handle 't' or 'T' suffix (trillion)
    if (cleaned.toLowerCase().endsWith('t')) {
      const num = parseFloat(cleaned.slice(0, -1));
      return isNaN(num) ? 0 : num * 1000000000000;
    }

    // Handle 'm' or 'M' suffix (million)
    if (cleaned.toLowerCase().endsWith('m')) {
      const num = parseFloat(cleaned.slice(0, -1));
      return isNaN(num) ? 0 : num * 1000000;
    }

    // Handle 'k' or 'K' suffix (thousand)
    if (cleaned.toLowerCase().endsWith('k')) {
      const num = parseFloat(cleaned.slice(0, -1));
      return isNaN(num) ? 0 : num * 1000;
    }

    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };


  // Update range boundaries if values exceed them
  useEffect(() => {
    if (minValue < rangeMin) {
      setRangeMin(minValue);
    }
    if (maxValue > rangeMax) {
      const newMax = maxLimit ? Math.min(maxValue, maxLimit) : maxValue;
      setRangeMax(newMax);
    }
  }, [minValue, maxValue, rangeMin, rangeMax, maxLimit]);

  // Call onChange callback when values change
  useEffect(() => {
    // Skip the initial mount to avoid calling onChange on component load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onChange) {
      const finalMinValue = showNoMin ? null : minValue;
      const finalMaxValue = showNoMax ? null : maxValue;
      onChange({ min: finalMinValue, max: finalMaxValue });
    }
  }, [minValue, maxValue, showNoMin, showNoMax, onChange]);

  // Handle min slider change
  const handleMinSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Reset range to default on first drag interaction
    if (shouldResetRangeOnDrag.current) {
      setRangeMin(propMin);
      setRangeMax(propMax);
      shouldResetRangeOnDrag.current = false;

      // Process the drag event with the reset range
      const value = Number(e.target.value);
      const clampedValue = Math.max(propMin, Math.min(value, propMax));
      const newMinValue = Math.min(clampedValue, Math.min(maxValue, propMax));
      setMinValue(newMinValue);
      setMaxValue(Math.min(maxValue, propMax));
      setLastChangedThumb('min');

      const threshold = propMin + (propMax - propMin) * (THRESHOLD_PERCENT / 100);
      if (newMinValue <= threshold) {
        setShowNoMin(true);
        setMinInputValue("");
      } else {
        setShowNoMin(false);
        setMinInputValue(formatInputCurrency(newMinValue));
      }
      return;
    }

    const value = Number(e.target.value);
    const newMinValue = Math.min(value, maxValue);
    setMinValue(newMinValue);
    setLastChangedThumb('min'); // Track that min thumb was last changed

    // Check if at the very low end (2% threshold)
    const threshold = rangeMin + (rangeMax - rangeMin) * (THRESHOLD_PERCENT / 100);
    if (newMinValue <= threshold) {
      setShowNoMin(true);
      setMinInputValue("");
    } else {
      setShowNoMin(false);
      setMinInputValue(formatInputCurrency(newMinValue));
    }
  };

  // Handle max slider change
  const handleMaxSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Reset range to default on first drag interaction
    if (shouldResetRangeOnDrag.current) {
      setRangeMin(propMin);
      setRangeMax(propMax);
      shouldResetRangeOnDrag.current = false;

      // Process the drag event with the reset range
      const value = Number(e.target.value);
      const clampedValue = Math.max(propMin, Math.min(value, propMax));
      const newMaxValue = Math.max(clampedValue, Math.min(minValue, propMax));
      setMaxValue(newMaxValue);
      setMinValue(Math.min(minValue, propMax));
      setLastChangedThumb('max');

      const threshold = propMax - (propMax - propMin) * (THRESHOLD_PERCENT / 100);
      if (newMaxValue >= threshold) {
        setShowNoMax(true);
        setMaxInputValue("");
      } else {
        setShowNoMax(false);
        setMaxInputValue(formatInputCurrency(newMaxValue));
      }
      return;
    }

    const value = Number(e.target.value);
    const newMaxValue = Math.max(value, minValue);
    setMaxValue(newMaxValue);
    setLastChangedThumb('max'); // Track that max thumb was last changed

    // Check if at the very high end (2% threshold)
    const threshold = rangeMax - (rangeMax - rangeMin) * (THRESHOLD_PERCENT / 100);
    if (newMaxValue >= threshold) {
      setShowNoMax(true);
      setMaxInputValue("");
    } else {
      setShowNoMax(false);
      setMaxInputValue(formatInputCurrency(newMaxValue));
    }
  };

  // Handle min input field change
  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Mark that range should reset on next drag
    shouldResetRangeOnDrag.current = true;

    if (value === "") {
      setMinInputValue("");
      setShowNoMin(true);
      setMinValue(rangeMin);
      setLastChangedThumb('min'); // Track that min was last changed
      return;
    }

    // Allow only numbers, commas, dots, and 'k', 'm', 't' suffix
    if (!/^[\d,\.kmt]*$/i.test(value)) return;

    setMinInputValue(value);
    const numValue = parseCurrency(value);

    // If NaN or invalid, reset to default
    if (isNaN(numValue) || numValue < 0) {
      setMinInputValue("");
      setShowNoMin(true);
      setMinValue(propMin);
      setLastChangedThumb('min');
      return;
    }

    if (numValue >= 0) {
      setShowNoMin(false);
      setLastChangedThumb('min'); // Track that min was last changed

      // Check if value exceeds maxLimit
      if (maxLimit && numValue > maxLimit) {
        setMinValue(maxLimit);
        setMinInputValue(formatInputCurrency(maxLimit));
        // Extend range to maxLimit
        if (maxLimit > rangeMax) {
          setRangeMax(maxLimit);
        }
        return;
      }

      // If user enters value less than current min range, extend the range
      if (numValue < rangeMin) {
        setRangeMin(numValue);
      }

      // If user enters value larger than current max range, extend the range
      if (numValue > rangeMax) {
        setRangeMax(numValue);
      }

      // Set the min value
      setMinValue(numValue);

      // If min value is greater than current max value, update max to match min
      if (numValue > maxValue) {
        setMaxValue(numValue);
      }
    }
  };

  // Handle max input field change
  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Mark that range should reset on next drag
    shouldResetRangeOnDrag.current = true;

    if (value === "") {
      setMaxInputValue("");
      setShowNoMax(true);
      setMaxValue(rangeMax);
      setLastChangedThumb('max'); // Track that max was last changed
      return;
    }

    // Allow only numbers, commas, dots, and 'k', 'm', 't' suffix
    if (!/^[\d,\.kmt]*$/i.test(value)) return;

    setMaxInputValue(value);
    const numValue = parseCurrency(value);

    // If NaN or invalid, reset to default
    if (isNaN(numValue) || numValue < 0) {
      setMaxInputValue("");
      setShowNoMax(true);
      setMaxValue(propMax);
      setLastChangedThumb('max');
      return;
    }

    if (numValue >= 0) {
      setShowNoMax(false);
      setLastChangedThumb('max'); // Track that max was last changed

      // Check if value exceeds maxLimit
      if (maxLimit && numValue > maxLimit) {
        setMaxValue(maxLimit);
        setMaxInputValue(formatInputCurrency(maxLimit));
        // Extend range to maxLimit
        if (maxLimit > rangeMax) {
          setRangeMax(maxLimit);
        }
        return;
      }

      // If user enters value larger than current max range, extend the range
      if (numValue > rangeMax) {
        setRangeMax(numValue);
      }

      // Only update thumb position if value is valid (greater than or equal to min)
      // This keeps the thumb in place while user is typing an invalid intermediate value
      if (showNoMin || numValue >= minValue) {
        setMaxValue(numValue);
      }
      // If invalid, keep current maxValue but show what user is typing in input
    }
  };

  // Handle min input focus (convert to number)
  const handleMinInputFocus = () => {
    setMinInputFocused(true);
    if (!showNoMin && minInputValue) {
      const numValue = parseCurrency(minInputValue);
      setMinInputValue(String(numValue));
    }
  };

  // Handle min input blur (format as currency with commas)
  const handleMinInputBlur = () => {
    setMinInputFocused(false);

    if (minInputValue === "" || showNoMin) {
      return;
    }

    const numValue = parseCurrency(minInputValue);

    // If NaN, reset to default
    if (isNaN(numValue)) {
      setMinInputValue("");
      setShowNoMin(true);
      setMinValue(propMin);
      return;
    }

    // Format as comma-separated currency for input field
    setMinInputValue(formatInputCurrency(numValue));
  };

  // Handle max input focus (convert to number)
  const handleMaxInputFocus = () => {
    setMaxInputFocused(true);
    if (!showNoMax && maxInputValue) {
      const numValue = parseCurrency(maxInputValue);
      setMaxInputValue(String(numValue));
    }
  };

  // Handle max input blur (validate when user leaves the field)
  const handleMaxInputBlur = () => {
    setMaxInputFocused(false);

    // If empty, keep as "No Max"
    if (maxInputValue === "") {
      return;
    }

    const numValue = parseCurrency(maxInputValue);

    // If NaN, reset to default
    if (isNaN(numValue)) {
      setMaxInputValue("");
      setShowNoMax(true);
      setMaxValue(propMax);
      return;
    }

    // Validate: max value cannot be less than min value (unless min is "No Min")
    if (!showNoMin && numValue < minValue) {
      setMaxValue(minValue);
      setMaxInputValue(formatInputCurrency(minValue));
      return;
    }

    // Format as comma-separated currency for input field
    setMaxInputValue(formatInputCurrency(numValue));
  };

  // Handle reset
  const handleReset = () => {
    setRangeMin(propMin);
    setRangeMax(propMax);
    setMinValue(propMin);
    setMaxValue(propMax);
    setMinInputValue("");
    setMaxInputValue("");
    setShowNoMin(true);
    setShowNoMax(true);
    shouldResetRangeOnDrag.current = true; // Mark that range should reset on next drag

    // Call external reset callback if provided
    if (externalReset) {
      externalReset();
    }
  };
  // Check if at least one value is selected / generated
  const hasSelectedValue =
    (!showNoMin && minValue !== null && minValue !== undefined && minValue > 0) ||
    (!showNoMax && maxValue !== null && maxValue !== undefined && maxValue > 0);

  const isApplyDisabled = !hasSelectedValue;

  // Handle apply
  const handleApply = () => {
    if (isApplyDisabled) return;

    const finalMinValue = showNoMin ? null : minValue;
    const finalMaxValue = showNoMax ? null : maxValue;

    if (onApply) {
      onApply({ min: finalMinValue, max: finalMaxValue });
    }
  };

  // Calculate slider track styling
  const getSliderTrackStyle = () => {
    const minPercent = ((minValue - rangeMin) / (rangeMax - rangeMin)) * 100;
    const maxPercent = ((maxValue - rangeMin) / (rangeMax - rangeMin)) * 100;

    return {
      left: `${Math.max(0, minPercent)}%`,
      width: `${Math.max(0, maxPercent - minPercent)}%`,
    };
  };

  return (
    <div className="price-range-wrapper">
      {/* Header */}
      <div className="price-range-header">
        <div className="price-range-label">
          {label}
        </div>
        <button className="price-range-reset" onClick={handleReset}>
          Clear
        </button>
      </div>

      {/* Range Slider */}
      <div className="price-range-slider">
        <div className="slider-track-bg">
          <div className="slider-track-active" style={getSliderTrackStyle()} />
        </div>

        {/* Min Thumb */}
        <input
          ref={minSliderRef}
          type="range"
          min={rangeMin}
          max={rangeMax}
          value={minValue}
          onChange={handleMinSliderChange}
          className={`slider-thumb slider-thumb-min`}
          style={{
            zIndex: lastChangedThumb === 'min' ? 5 : 3,
            transform: Math.abs(maxValue - minValue) < MIN_GAP * 0.2 && lastChangedThumb === 'min'
              ? 'translateX(-2px)'
              : undefined
          }}
          step="1"
        />

        {/* Max Thumb */}
        <input
          ref={maxSliderRef}
          type="range"
          min={rangeMin}
          max={rangeMax}
          value={maxValue}
          onChange={handleMaxSliderChange}
          className={`slider-thumb slider-thumb-max`}
          style={{
            zIndex: lastChangedThumb === 'max' ? 5 : 4,
            transform: Math.abs(maxValue - minValue) < MIN_GAP * 0.2 && lastChangedThumb === 'max'
              ? 'translateX(2px)'
              : undefined
          }}
          step="1"
        />
      </div>
      <div>
        <p className="text-xs font-normal py-2 text-gray-600">
          {type === "desk" ? (
            <>
              Select your preferred <span className="text-black font-medium">desk range</span>.
            </>
          ) : (
            <>
              Enter your minimum and maximum <span className="text-black font-medium">{type.toLowerCase()} / month</span>.
            </>
          )}
        </p>
      </div>
      {/* Input Fields */}
      <div className="price-range-inputs">
        <div className="price-input-group">
          <div className="price-input-wrapper">
            {prefix && <span className={inputClassName || "price-input-prefix"}>{prefix}</span>}
            <input
              className="price-input-field"
              placeholder={showNoMin ? `Min. ${type}` : String(minValue)}
              value={minInputValue}
              onChange={handleMinInputChange}
              onFocus={handleMinInputFocus}
              onBlur={handleMinInputBlur}
              type="text"
            />
          </div>
        </div>

        <div className="price-input-group">
          <div className="price-input-wrapper">
            {prefix && <span className={inputClassName || "price-input-prefix"}>{prefix}</span>}
            <input
              type="text"
              className="price-input-field"
              placeholder={showNoMax ? `Max. ${type}` : String(maxValue)}
              value={maxInputValue}
              onChange={handleMaxInputChange}
              onFocus={handleMaxInputFocus}
              onBlur={handleMaxInputBlur}
            />
          </div>
        </div>
      </div>


      {/* Apply Button */}
      {showApplyButton && (
        <button
          className="price-range-apply"
          onClick={handleApply}
          disabled={isApplyDisabled}
        >
          Apply
        </button>
      )}
    </div>
  );
};

export default RangeSlider;

